/**
 * Copyright ©2022 Drivepoint
 */

import {useEffect, useState} from "react";
import DrivepointUser from "@services/user/DrivepointUser";
import ServiceRegistry from "../services/ServiceRegistry";
import usePathContext from "./usePathContext";
import useStateChange from "./useStateChange";

export type RoutesProps = {
  pages?: any[];
  sections?: any[];
};

export default function useRoutes(navigation: boolean = false): RoutesProps {

  const flags = useStateChange<any>("flags");
  const pathContext = usePathContext();
  const user = useStateChange<DrivepointUser>("user");
  const company = useStateChange<any>("company");
  const routes = useStateChange<any>("routes");

  const [visiblePages, setVisiblePages] = useState<any[]>();
  const [visibleSections, setVisibleSections] = useState<any[]>();

  useEffect(() => {
    const pages = routes?.pages || ServiceRegistry.routeService.pages;
    const sections = routes?.sections || ServiceRegistry.routeService.sections;
    setVisiblePages(pages.filter((page: any) => isPageVisible(page, sections)));
    setVisibleSections(sections.map((section: any) => getVisibleSection(section)).filter((it: any) => it));
  }, [routes, company?.id, user, flags]);

  /**
   * Check to see if a company's tier allows access to this page. A page can have an array of tiers, both
   * included and excluded tiers. An excluded tier is prefixed with an exclamation mark.
   */
  function companyCanAccessTier(page: any): boolean {
    if (!page.tiers?.length) { return true; } // no tiers, page is implicitly included
    if (!company) { return false; } // no company, no access
    // get any excluded tiers, and reject this page if company matches an excluded tier:
    const excludeTiers = page.tiers
      .filter((tier: string) => tier.startsWith("!"))
      .map((tier: string) => tier.replace(/^!/, ""));
    if (excludeTiers.includes(company.tier)) { return false; }
    // get any included tiers, and reject this page if there are tiers, but the company doesn't match one
    const includeTiers = page.tiers.filter((tier: string) => !tier.startsWith("!"));
    if (!includeTiers.length) { return true; } // no included tiers, page is implicitly included
    return includeTiers.includes(company.tier);
  }

  function isVisible(type: "page" | "section", id?: string): boolean {
    if (!id) { return true; }
    const directKey = `${type}.${id}.enabled`;
    if (flags[directKey] === false) { return false; }
    if (pathContext.companyId) { // TODO: deprecate in favor of LaunchDarkly company rule
      const companyKey = `${type}.${pathContext.companyId}.${id}.enabled`;
      if (flags[companyKey] !== undefined) { return flags[companyKey]; }
    }
    const allCompanyKey = `${type}.ALL.${id}.enabled`; // TODO: deprecate the "ALL" flags in favor of LaunchDarkly rules
    return flags[allCompanyKey] !== undefined ? flags[allCompanyKey] : true;
  }

  function isPageVisible(page: any, sections: any[] = []): boolean {
    if (page.public) { return true; }
    // auth isn't ready, or user isn't fully loaded:
    if (!user) { return false; }
    // page is hidden from nav:
    if (navigation && page.hidden) { return false; }
    if (page.parent) {
      const parentSection = sections.find((section: any) => section.id === page.parent);
      if (parentSection) {
        if (!isSectionVisible(parentSection)) { return false; }
      } else {
        const allPages = routes?.pages || ServiceRegistry.routeService.pages;
        const parentPage = allPages.find((p: any) => p.id === page.parent);
        if (parentPage && !isPageVisible(parentPage, sections)) { return false; }
      }
    }
    if (page.companyFlagKey && !company?.[page.companyFlagKey]) { return false; }
    const isManager = user?.excelUser?.role === "admin" || user?.excelUser?.role === "superAdmin";
    if (flags?.["hierarchical.acl.enabled"] && page.featureAccessKey && !isManager && company?.featuresAccess) {
      if (!company.featuresAccess[page.featureAccessKey]) { return false; }
    } else if (page.ldFlagKey && !flags?.[page.ldFlagKey]) {
      return false;
    }
    if (!isVisible("page", page.id)) { return false; }
    if (!companyCanAccessTier(page)) { return false; }
    return true;
  }

  function isSectionVisible(section: any): boolean {
    return isVisible("section", section?.id);
  }

  function getVisibleSection(section: any): any {
    // auth isn't ready, or user isn't fully loaded:
    if (!user) { return undefined; }
    // section is hidden:
    if (section.hidden) { return undefined; }
    if (!isSectionVisible(section)) { return undefined; }
    // find all visible pages for this section, if any:
    const pages = section.pages.filter((page: any) => isPageVisible(page));
    if (!pages.length) { return undefined; }
    // rebuild the section object with just the visible pages and return:
    return Object.assign({}, section, {pages: pages});
  }

  return {
    pages: visiblePages,
    sections: visibleSections
  };

};
